import { GlobalShortcutKey } from 'hooks/keyboard-shortcuts/global-shortcut-keys'
import {
  type KeyboardShortcutActions,
  KeyboardShortcutOptions,
  useKeyboardShortcuts,
} from 'hooks/use-keyboard-shortcuts'

/**
 * Hook for feature pages implementing reserved global shortcuts.
 *
 * Use this when your feature page needs to handle a global shortcut
 * like NESTED_SIDEBAR_TOGGLE that is reserved but not handled at layout level.
 *
 * This hook is specifically for implementing reserved global shortcuts at the
 * feature level (e.g., '2' for sidebar toggle on pages with sidebars).
 *
 * @param key - The reserved global shortcut key to implement
 * @param callback - Function to call when the shortcut is triggered
 * @param options - Configuration options for shortcut behavior
 *
 * @example
 * ```tsx
 * // In a feature page with a sidebar
 * useGlobalShortcut(
 *   GLOBAL_SHORTCUT_KEYS.NESTED_SIDEBAR_TOGGLE,
 *   toggleNestedSidebar,
 *   { respectInputs: true, enabled: true }
 * )
 * ```
 */
export const useGlobalShortcut = (
  key: GlobalShortcutKey,
  callback: () => void,
  options: KeyboardShortcutOptions = {},
): KeyboardShortcutActions => {
  return useKeyboardShortcuts({ [key]: callback }, options)
}
